import axios from 'axios';

/**
 * Crea un'istanza di Axios configurata
 * @param {string} token - Token di autenticazione
 * @returns {object} - Istanza di Axios configurata
 */
export const createApiClient = (token) => {
  const instance = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || 'http://192.168.1.113/api',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    }
  });

  // Aggiungi interceptor per gestire errori 401
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401) {
        // Token non valido, emetti un evento che può essere catturato
        // dal context di autenticazione
        window.dispatchEvent(new CustomEvent('auth:invalid-token'));
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

/**
 * API per i gruppi muscolari
 */
export const muscleGroupsApi = {
  /**
   * Recupera tutti i gruppi muscolari
   * @param {object} params - Parametri opzionali {category, active_only, group_by_category}
   * @returns {Promise} - Promise con i dati dei muscoli
   */
  getAll: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.category) queryParams.append('category', params.category);
      if (params.active_only !== undefined) queryParams.append('active_only', params.active_only);
      if (params.group_by_category) queryParams.append('group_by_category', params.group_by_category);
      
      const url = `/api/muscle_groups.php${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        return data.data;
      } else {
        throw new Error(data.message || 'Errore nel recupero dei muscoli');
      }
    } catch (error) {
      console.error('Errore API muscle_groups:', error);
      throw error;
    }
  },

  /**
   * Recupera un muscolo specifico per ID
   * @param {number} id - ID del muscolo
   * @returns {Promise} - Promise con i dati del muscolo
   */
  getById: async (id) => {
    try {
      const muscles = await muscleGroupsApi.getAll();
      return muscles.find(m => m.id === parseInt(id)) || null;
    } catch (error) {
      console.error('Errore API muscle_groups getById:', error);
      throw error;
    }
  },

  /**
   * Recupera muscoli per categoria
   * @param {string} category - Categoria (es: 'Gambe', 'Petto')
   * @returns {Promise} - Promise con array di muscoli
   */
  getByCategory: async (category) => {
    return muscleGroupsApi.getAll({ category });
  }
};

/**
 * API per le relazioni esercizi-muscoli
 */
export const exerciseMusclesApi = {
  /**
   * Recupera i muscoli di un esercizio
   * @param {number} exerciseId - ID dell'esercizio
   * @param {object} params - Parametri opzionali {is_primary, include_exercise_info}
   * @returns {Promise} - Promise con array di relazioni
   */
  getByExercise: async (exerciseId, params = {}) => {
    try {
      const queryParams = new URLSearchParams({ exercise_id: exerciseId });
      if (params.is_primary !== undefined) queryParams.append('is_primary', params.is_primary);
      if (params.include_exercise_info) queryParams.append('include_exercise_info', params.include_exercise_info);
      
      const response = await fetch(`/api/exercise_muscles.php?${queryParams.toString()}`);
      const data = await response.json();
      
      if (data.success) {
        return data.data;
      } else {
        throw new Error(data.message || 'Errore nel recupero dei muscoli dell\'esercizio');
      }
    } catch (error) {
      console.error('Errore API exercise_muscles:', error);
      throw error;
    }
  },

  /**
   * Recupera gli esercizi che allenano un muscolo specifico
   * @param {number} muscleId - ID del muscolo
   * @returns {Promise} - Promise con array di relazioni
   */
  getByMuscle: async (muscleId) => {
    try {
      const response = await fetch(`/api/exercise_muscles.php?muscle_id=${muscleId}`);
      const data = await response.json();
      
      if (data.success) {
        return data.data;
      } else {
        throw new Error(data.message || 'Errore nel recupero degli esercizi per il muscolo');
      }
    } catch (error) {
      console.error('Errore API exercise_muscles getByMuscle:', error);
      throw error;
    }
  },

  /**
   * Crea una nuova relazione esercizio-muscolo
   * @param {object} data - Dati {exercise_id, muscle_group_id, is_primary, activation_level, notes}
   * @returns {Promise} - Promise con risultato
   */
  create: async (data) => {
    try {
      const response = await fetch('/api/exercise_muscles.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Errore nella creazione della relazione');
      }
      
      return result;
    } catch (error) {
      console.error('Errore API exercise_muscles create:', error);
      throw error;
    }
  },

  /**
   * Aggiorna una relazione esistente
   * @param {number} id - ID della relazione
   * @param {object} data - Dati da aggiornare
   * @returns {Promise} - Promise con risultato
   */
  update: async (id, data) => {
    try {
      const response = await fetch(`/api/exercise_muscles.php?id=${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Errore nell\'aggiornamento della relazione');
      }
      
      return result;
    } catch (error) {
      console.error('Errore API exercise_muscles update:', error);
      throw error;
    }
  },

  /**
   * Elimina una relazione
   * @param {number} id - ID della relazione
   * @returns {Promise} - Promise con risultato
   */
  delete: async (id) => {
    try {
      const response = await fetch(`/api/exercise_muscles.php?id=${id}`, {
        method: 'DELETE'
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Errore nell\'eliminazione della relazione');
      }
      
      return result;
    } catch (error) {
      console.error('Errore API exercise_muscles delete:', error);
      throw error;
    }
  }
};